'use client';

import { useEffect, useRef, useState } from 'react';
import { centsOffFrom, detectPitch, freqToNote } from '@/lib/browser-pitch-detect';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type TunerVariant =
  | 'guitar-tuner'
  | 'bass-tuner'
  | 'ukulele-tuner'
  | 'violin-tuner'
  | 'piano-tuner';

interface StringDef {
  name: string;
  freq: number;
}

interface InstrumentConfig {
  title: string;
  badge: string;
  strings: StringDef[] | null; // null = chromatic (piano): any note, no fixed reference set
  minHz: number;
  maxHz: number;
  howTo: string;
}

const INSTRUMENTS: Record<TunerVariant, InstrumentConfig> = {
  'guitar-tuner': {
    title: 'Guitar tuner',
    badge: 'Guitar',
    strings: [
      { name: 'E2', freq: 82.41 },
      { name: 'A2', freq: 110.0 },
      { name: 'D3', freq: 146.83 },
      { name: 'G3', freq: 196.0 },
      { name: 'B3', freq: 246.94 },
      { name: 'E4', freq: 329.63 },
    ],
    minHz: 55,
    maxHz: 420,
    howTo: 'Standard tuning, low to high: E A D G B E. Pluck one string at a time.',
  },
  'bass-tuner': {
    title: 'Bass tuner',
    badge: 'Bass',
    strings: [
      { name: 'E1', freq: 41.2 },
      { name: 'A1', freq: 55.0 },
      { name: 'D2', freq: 73.42 },
      { name: 'G2', freq: 98.0 },
    ],
    minHz: 28,
    maxHz: 260,
    howTo: 'Standard 4-string tuning, low to high: E A D G. Pluck one string at a time.',
  },
  'ukulele-tuner': {
    title: 'Ukulele tuner',
    badge: 'Ukulele',
    strings: [
      { name: 'G4', freq: 392.0 },
      { name: 'C4', freq: 261.63 },
      { name: 'E4', freq: 329.63 },
      { name: 'A4', freq: 440.0 },
    ],
    minHz: 220,
    maxHz: 500,
    howTo: 'Standard reentrant tuning: G C E A. Pluck one string at a time.',
  },
  'violin-tuner': {
    title: 'Violin tuner',
    badge: 'Violin',
    strings: [
      { name: 'G3', freq: 196.0 },
      { name: 'D4', freq: 293.66 },
      { name: 'A4', freq: 440.0 },
      { name: 'E5', freq: 659.25 },
    ],
    minHz: 160,
    maxHz: 750,
    howTo: 'Standard tuning, low to high: G D A E. Bow or pluck one string at a time.',
  },
  'piano-tuner': {
    title: 'Piano tuner',
    badge: 'Piano',
    strings: null,
    minHz: 27,
    maxHz: 1500,
    howTo:
      'Chromatic mode: play any single note or key and it is identified automatically. Most reliable below the top two octaves.',
  },
};

// How far off (in cents) counts as "in tune". 100 cents = 1 semitone.
const IN_TUNE_CENTS = 5;
const CLOSE_CENTS = 15;

function nearestString(freq: number, strings: StringDef[]): { string: StringDef; cents: number } {
  let best = strings[0];
  let bestCents = centsOffFrom(freq, best.freq);
  for (const s of strings.slice(1)) {
    const cents = centsOffFrom(freq, s.freq);
    if (Math.abs(cents) < Math.abs(bestCents)) {
      best = s;
      bestCents = cents;
    }
  }
  return { string: best, cents: bestCents };
}

function tuningColor(cents: number): string {
  const abs = Math.abs(cents);
  if (abs <= IN_TUNE_CENTS) return '#1baf7a';
  if (abs <= CLOSE_CENTS) return '#eda100';
  return '#e34948';
}

export default function BrowserInstrumentTuner({ variant }: { variant: TunerVariant }) {
  const config = INSTRUMENTS[variant];
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  const [status, setStatus] = useState<'idle' | 'listening' | 'denied' | 'error'>('idle');
  const [noteName, setNoteName] = useState<string | null>(null);
  const [noteOctave, setNoteOctave] = useState<number | null>(null);
  const [detectedFreq, setDetectedFreq] = useState<number | null>(null);
  const [cents, setCents] = useState(0);
  const [activeStringName, setActiveStringName] = useState<string | null>(null);

  function stop() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void contextRef.current?.close().catch(() => undefined);
    contextRef.current = null;
    setStatus('idle');
    setNoteName(null);
    setNoteOctave(null);
    setDetectedFreq(null);
    setActiveStringName(null);
  }

  async function start() {
    try {
      // Disable the browser's own speech-call audio processing: echo cancellation,
      // noise suppression and auto-gain all reshape the waveform in ways that throw
      // off pitch detection, and none of them help a single sustained musical note.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      streamRef.current = stream;
      setStatus('listening');
      void consumeToolUsage(variant, '').catch(() => undefined);

      const audioContext = new AudioContext();
      contextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 4096;
      source.connect(analyser);
      const buffer = new Float32Array(analyser.fftSize);

      const tick = () => {
        analyser.getFloatTimeDomainData(buffer);
        const freq = detectPitch(buffer, audioContext.sampleRate);

        if (freq !== null && freq >= config.minHz && freq <= config.maxHz) {
          if (config.strings) {
            const { string, cents: offCents } = nearestString(freq, config.strings);
            setActiveStringName(string.name);
            setNoteName(string.name.replace(/\d+$/, ''));
            setNoteOctave(Number(string.name.match(/\d+$/)?.[0] ?? 0));
            setCents(offCents);
          } else {
            const note = freqToNote(freq);
            setNoteName(note.name);
            setNoteOctave(note.octave);
            setCents(note.cents);
          }
          setDetectedFreq(freq);
        }

        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (error) {
      setStatus(
        error instanceof DOMException && error.name === 'NotAllowedError' ? 'denied' : 'error',
      );
    }
  }

  useEffect(() => {
    return () => stop();
  }, []);

  const clampedCents = Math.max(-50, Math.min(50, cents));
  const needleColor = tuningColor(cents);
  const tuningLabel =
    detectedFreq === null
      ? 'Play a note'
      : Math.abs(cents) <= IN_TUNE_CENTS
        ? 'In tune'
        : cents < 0
          ? 'Flat, tune up'
          : 'Sharp, tune down';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{config.badge}</span>
        <h2 className="tool-live-title">{config.title}</h2>
        <p className="tool-live-subtitle">
          Play a note and the nearest pitch is identified live from your microphone. Reference pitch
          A4 = 440 Hz. Nothing is recorded, nothing leaves your device.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader tuner-panel">
          <div className="tuner-readout" style={{ color: detectedFreq ? needleColor : undefined }}>
            <span className="tuner-note">
              {noteName ?? '—'}
              {noteOctave !== null && noteName ? (
                <sub className="tuner-octave">{noteOctave}</sub>
              ) : null}
            </span>
            <span className="tuner-label">{tuningLabel}</span>
          </div>

          <div className="tuner-meter" aria-hidden="true">
            <div className="tuner-meter-zone" />
            {detectedFreq !== null ? (
              <div
                className="tuner-meter-needle"
                style={{
                  left: `${50 + clampedCents}%`,
                  background: needleColor,
                }}
              />
            ) : null}
          </div>

          <p className="tool-live-panel-note">
            {detectedFreq !== null
              ? `${detectedFreq.toFixed(1)} Hz, ${cents > 0 ? '+' : ''}${cents} cents`
              : 'Waiting for a clear, sustained note.'}
          </p>

          {status === 'denied' ? (
            <p className="support-feedback support-feedback-error">
              Microphone access was blocked. Allow it in the address-bar permission prompt, then
              press Start again. The audio still never leaves this page.
            </p>
          ) : null}
          {status === 'error' ? (
            <p className="support-feedback support-feedback-error">
              No microphone was found, or the browser could not open it. Check that a mic is
              connected and not in use by another app.
            </p>
          ) : null}

          <div className="tool-live-actions">
            {status === 'listening' ? (
              <button className="button button-ghost" onClick={stop} type="button">
                Stop
              </button>
            ) : (
              <button className="button" onClick={() => void start()} type="button">
                Start tuning
              </button>
            )}
          </div>
        </section>

        <aside className="tool-live-panel">
          <p className="tool-live-panel-note">{config.howTo}</p>
          {config.strings ? (
            <div className="tuner-strings">
              {config.strings.map((s) => (
                <span
                  className={`tuner-string-chip${activeStringName === s.name ? ' tuner-string-chip-active' : ''}`}
                  key={s.name}
                >
                  {s.name}
                  <small>{s.freq.toFixed(2)} Hz</small>
                </span>
              ))}
            </div>
          ) : null}
          <p className="tool-live-panel-note">
            Tune in a quiet room, one note at a time. Green means within 5 cents of the target,
            amber within 15 cents, red further off.
          </p>
        </aside>
      </div>
    </div>
  );
}
